import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "../config/api";
import { checkTokenValidity } from "../utils/auth";
import LoadingScreen from "../components/LoadingScreen";
import { createPortal } from "react-dom";
import React from "react";
// import { Link } from "react-router-dom";
import {
	TrashIcon,
	ArrowUturnLeftIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Button as MuiButton, Paper } from "@mui/material";
import DatePickerField from "../components/DatePickerField";
import "react-datepicker/dist/react-datepicker.css";
import OrderSearchModal from "../components/OrderSearchModal";
import LoadingOverlay from "../components/LoadingOverlay";

const MaterialSearchIcon = ({ onClick }) => (
	<button
		onClick={onClick}
		className="w-4 h-4 text-gray-500 hover:text-gray-700"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
			/>
		</svg>
	</button>
);

const ClearIcon = ({ onClick }) => (
	<button
		onClick={onClick}
		className="w-4 h-4 text-gray-500 hover:text-gray-700"
	>
		<XMarkIcon className="h-4 w-4" />
	</button>
);

const TextField = React.memo(
	({
		label,
		value,
		onChange,
		className = "",
		disabled = false,
		readOnly = false, // Add readOnly prop
		width = "full",
		rightIcon,
		maxLength,
		forceWhite = false,
		onClick,
	}) => {
		const handleChange = useCallback(
			(e) => {
				if (onChange) {
					const newValue = e.target.value.slice(0, maxLength);
					onChange(newValue);
				}
			},
			[onChange, maxLength]
		);

		// const handleClick = useCallback(
		// 	(e) => {
		// 		e.stopPropagation(); // Stop event propagation
		// 		if (onClick) {
		// 			onClick(e);
		// 		}
		// 	},
		// 	[onClick]
		// );

		// Create a wrapper component that handles the click
		// const InputWrapper = ({ children }) => {
		// 	if (onClick) {
		// 		return (
		// 			<div
		// 				className="relative cursor-pointer"
		// 				onClick={handleClick}
		// 				role="button"
		// 				tabIndex={0}
		// 			>
		// 				{children}
		// 			</div>
		// 		);
		// 	}
		// 	return <div className="relative">{children}</div>;
		// };

		return (
			<div className={`${className} ${width === "full" ? "w-full" : ""}`}>
				<label className="text-sm font-medium text-gray-500">{label}</label>
				<div className="relative">
					<input
						type="text"
						value={value || ""}
						onChange={handleChange}
						onClick={onClick}
						disabled={disabled}
						readOnly={readOnly} // Add readOnly attribute
						maxLength={maxLength}
						className={`mt-0.5 block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm text-sm
						${
							(disabled || readOnly) && !forceWhite
								? "bg-gray-100 text-gray-500"
								: "bg-white text-gray-900"
						} 
						${rightIcon ? "pr-8" : ""}
						${onClick ? "cursor-pointer" : ""}`}
					/>
					{rightIcon && (
						<div className="absolute inset-y-0 right-0 flex items-center pr-2 mt-0.5">
							{rightIcon}
						</div>
					)}
				</div>
			</div>
		);
	}
);

const SparepartDetail = () => {
	const { inqno } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);
	const [isEditing, setIsEditing] = useState(false);
	const [selectedItem, setSelectedItem] = useState(0); // Index of selected item
	const [category, setCategory] = useState(""); // Add this state
	const [isLocked, setIsLocked] = useState(false);
	const [lockError, setLockError] = useState(null);
	const [alertDialog, setAlertDialog] = useState({
		isOpen: false,
		message: "",
		type: "error", // or "success"
	});
	const [isMaterialSearchOpen, setIsMaterialSearchOpen] = useState(false);
	const [isOrderSearchOpen, setIsOrderSearchOpen] = useState(false);
	const [isStorageLocationOpen, setIsStorageLocationOpen] = useState(false);
	const [originalData, setOriginalData] = useState(null);
	const [originalItems, setOriginalItems] = useState([]);
	const [newItemData, setNewItemData] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	const removeLeadingZeros = (str) => {
		if (!str) return str;
		return str.replace(/^0+/, "");
	};

	const fetchSparepartDetail = async () => {
		try {
			const response = await axios.get(getApiUrl(`/sparepart/${inqno}`), {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			});

			// Filter out duplicate items based on inqit
			const uniqueItems = response.data.mt_item.reduce((acc, current) => {
				const isDuplicate = acc.find((item) => item.inqit === current.inqit);
				if (!isDuplicate) {
					// Ensure each item has plant and storage location from header
					acc.push({
						...current,
						werks: response.data.ms_header.werks_cid,
						lgort: response.data.ms_header.lgort_cid,
					});
				}
				return acc;
			}, []);

			// Update the response data with unique items
			const cleanedData = {
				...response.data,
				mt_item: uniqueItems,
			};

			setData(cleanedData);
			setSelectedItem(0);

			// Set category based on inqno prefix
			if (inqno.startsWith("M")) {
				setCategory("CGI"); // Changed from empty string to "CGI"
			} else {
				setCategory(response.data.ms_header.type || "AG");
			}

			setOriginalData(cleanedData);
			setOriginalItems(cleanedData.mt_item);
		} catch (err) {
			setError("Failed to fetch sparepart details");
			if (err.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		} finally {
			setLoading(false);
		}
	};

	// Fix for useEffect with fetchSparepartDetail dependency
	useEffect(() => {
		const validateTokenAndFetch = async () => {
			setLoading(true);
			const isValid = await checkTokenValidity();
			if (!isValid) {
				navigate("/login");
				return;
			}
			fetchSparepartDetail();
		};

		validateTokenAndFetch();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inqno, navigate]);
	// Adding fetchSparepartDetail to dependencies would cause infinite loops
	// since it's defined in the component body

	// Add showAlert helper function
	const showAlert = (message, type = "error") => {
		setAlertDialog({
			isOpen: true,
			message,
			type,
		});

		// Automatically close after 3 seconds for success messages
		if (type === "success") {
			setTimeout(() => {
				setAlertDialog((prev) => ({ ...prev, isOpen: false }));
			}, 3000);
		}
	};

	// Function to lock the inquiry
	// const lockInquiry = async () => {
	// 	try {
	// 		const response = await axios.get(getApiUrl(`/lockinq/${inqno}`), {
	// 			headers: {
	// 				Authorization: `Bearer ${localStorage.getItem("token")}`,
	// 			},
	// 		});
	// 		if (response.status === 200) {
	// 			setIsLocked(true);
	// 			setIsEditing(true);
	// 			setLockError(null);
	// 		}
	// 	} catch (error) {
	// 		console.error("Error locking inquiry:", error);
	// 		showAlert("InqNo is currently being locked");
	// 		setIsEditing(false);
	// 		if (error.response?.status === 401) {
	// 			localStorage.removeItem("token");
	// 			navigate("/login");
	// 		}
	// 	}
	// };

	// Function to unlock the inquiry
	const unlockInquiry = async () => {
		try {
			await axios.get(getApiUrl(`/unlockinq/${inqno}`), {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			});
			setIsLocked(false);
			setIsEditing(false);
		} catch (error) {
			// console.error("Error unlocking inquiry:", error);
			showAlert("Failed to unlock inquiry");
			if (error.response?.status === 401) {
				localStorage.removeItem("token");
				navigate("/login");
			}
		}
	};

	// Add this validation function
	const validateForm = () => {
		if (
			!data.ms_header.werks_cid ||
			!data.ms_header.lgort_cid ||
			!data.mt_item?.length
		) {
			showAlert("Please fill in all required fields and add at least one item");
			return false;
		}

		// Check for empty quantities
		const emptyQuantities = data.mt_item.some(
			(item) => !item.menge || item.menge === "0" || item.menge === ""
		);
		if (emptyQuantities) {
			showAlert("Please enter valid quantities for all items");
			return false;
		}

		if (category === "IO") {
			const missingOrders = data.mt_item.some((item) => !item.aufnr);
			if (missingOrders) {
				showAlert("Order field is required for all items when category is IO");
				return false;
			}
		}

		return true;
	};

	// Modify handleEdit function to include inqno and inqit in payload while preserving item numbering
	const handleEdit = async () => {
		if (isEditing) {
			// Validate form before saving
			if (!validateForm()) {
				return;
			}

			try {
				setIsSaving(true); // Start saving state
				// Prepare payload in the same format as AddSparepart but include inqno and inqit
				const payload = {
					header: {
						inqno,
						plant: data.ms_header.werks_cid,
						storage_location: data.ms_header.lgort_cid,
						material_slip: data.ms_header.xblnr || "",
						bktxt: data.ms_header.bktxt || "",
						document_date: data.ms_header.bldat,
					},
					details: data.mt_item.map((item, index) => ({
						inqno: item.inqno, // Keep original inqno
						inqit: item.inqit, // Keep original inqit
						item: (index + 1).toString().padStart(2, "0"), // Keep sequential numbering
						material: item.matnr,
						material_description: item.maktx || "",
						quantity: Number(item.menge),
						uom: item.uom?.toUpperCase() === "PCS" ? "ST" : item.uom || "",
						wempf: item.wempf || "",
						order: item.aufnr || "",
						text: item.sgtxt || "",
						charg: item.charg || "",
						status: item.status || "", // Add status field
					})),
				};
				// console.log(payload);

				// Make PUT request to update sparepart
				await axios.put(getApiUrl(`/sparepart/${category}`), payload, {
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				});

				// Unlock the inquiry after successful save
				await unlockInquiry();

				// Show success message
				showAlert("Sparepart document updated successfully!", "success");

				// Refresh the data
				fetchSparepartDetail();
			} catch (error) {
				// console.error("Error submitting sparepart:", error);

				// Handle error response with messages
				if (
					error.response?.data?.message &&
					Array.isArray(error.response.data.message)
				) {
					const errorMessages = error.response.data.message;
					if (errorMessages.length > 0) {
						showAlert(
							<ul className="list-disc pl-5 space-y-1">
								{errorMessages.map((msg, index) => (
									<li key={index}>{msg}</li>
								))}
							</ul>
						);
					} else {
						showAlert("Failed to update sparepart document. Please try again.");
					}
				} else {
					showAlert("Failed to update sparepart document. Please try again.");
				}

				if (error.response?.status === 401) {
					localStorage.removeItem("token");
					navigate("/login");
				}
			} finally {
				setIsSaving(false); // End saving state
			}
		} else {
			try {
				// First check if inquiry is locked
				const response = await axios.get(getApiUrl(`/lockinq/${inqno}`), {
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				});

				if (response.status === 200) {
					// If successfully locked, fetch latest data
					await fetchSparepartDetail();

					// Then set editing state
					setIsLocked(true);
					setIsEditing(true);
					setLockError(null);
				}
			} catch (error) {
				// console.error("Error locking inquiry:", error);
				showAlert("InqNo is currently being locked");
				setIsEditing(false);
				if (error.response?.status === 401) {
					localStorage.removeItem("token");
					navigate("/login");
				}
			}
		}
	};

	// Fix for useEffect with unlockInquiry dependency
	useEffect(() => {
		return () => {
			// Cleanup: unlock if component unmounts while locked
			if (isLocked) {
				unlockInquiry();
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLocked]);
	// Adding unlockInquiry to dependencies could cause issues with cleanup

	// Add back handler
	const handleBack = async () => {
		if (isLocked) {
			await unlockInquiry();
		}
		navigate("/");
	};

	// Handle browser close/refresh
	useEffect(() => {
		const handleBeforeUnload = async (e) => {
			if (isLocked) {
				// Cancel the event
				e.preventDefault();
				// Chrome requires returnValue to be set
				e.returnValue = "";

				try {
					// Call unlock endpoint
					await axios.get(getApiUrl(`/unlockinq/${inqno}`), {
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					});
				} catch (error) {
					// console.error("Error unlocking on page unload:", error);
				}
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		// Cleanup function for route changes and component unmount
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);

			// If locked, make the unlock API call
			if (isLocked) {
				const unlockOnUnmount = async () => {
					try {
						await axios.get(getApiUrl(`/unlockinq/${inqno}`), {
							headers: {
								Authorization: `Bearer ${localStorage.getItem("token")}`,
							},
						});
					} catch (error) {
						// console.error("Error unlocking on unmount:", error);
					}
				};
				unlockOnUnmount();
			}
		};
	}, [inqno, isLocked]);

	// Add debounced state update
	const debouncedSetData = useCallback((updateFn) => {
		setTimeout(() => {
			setData(updateFn);
		}, 0);
	}, []);

	// Update handlers to use debounced state update
	const handleHeaderChange = useCallback(
		(field, value) => {
			debouncedSetData((prev) => ({
				...prev,
				ms_header: {
					...prev.ms_header,
					[field]: value,
				},
			}));
		},
		[debouncedSetData]
	);

	const handleItemChange = useCallback(
		(field, value) => {
			if (field === "menge" && !/^\d*\.?\d*$/.test(value)) {
				return; // Only allow numbers and decimal point
			}

			debouncedSetData((prev) => ({
				...prev,
				mt_item: prev.mt_item.map((item, idx) =>
					idx === selectedItem ? { ...item, [field]: value } : item
				),
			}));
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[selectedItem]
	);

	// Memoize the disabled state for TextField
	const isTextFieldDisabled = useMemo(() => !isEditing, [isEditing]);

	// Update the material search handler
	const handleMaterialSearchClick = () => {
		if (!isEditing) return;
		if (!data?.ms_header?.lgort_cid) {
			showAlert("Please select a Storage Location first");
			return;
		}
		setIsMaterialSearchOpen(true);
	};

	// Add these modal components after the TextField component

	const MaterialSearchModal = ({
		isOpen,
		onClose,
		onSelect,
		plant,
		storageLocation,
		category,
		existingItems,
	}) => {
		const [materials, setMaterials] = useState([]);
		const [searchTerm, setSearchTerm] = useState("");
		const [filteredMaterials, setFilteredMaterials] = useState([]);
		const [isLoading, setIsLoading] = useState(true);
		const [currentPage, setCurrentPage] = useState(1);
		const itemsPerPage = 10;

		useEffect(() => {
			const fetchMaterials = async () => {
				if (!isOpen || !storageLocation) {
					return;
				}

				try {
					setIsLoading(true);
					const response = await axios.get(
						getApiUrl(`/materials/${storageLocation}`),
						{
							headers: {
								Authorization: `Bearer ${localStorage.getItem("token")}`,
							},
						}
					);

					const data = response.data;
					const transformedData = data.map((item) => ({
						key: `${item.matnr}-${item.charg || ""}`, // Add unique key
						id: item.matnr,
						material: item.matnr,
						description: item.maktx,
						quantity: item.menge || 0,
						uom: item.uom || "ST",
						type: item.type,
						plant: item.werks,
						storage_location: item.lgort,
						batch: item.charg || "",
					}));

					// Update filtering logic based on category
					const availableMaterials = transformedData.filter((material) => {
						if (category === "CGI") {
							// For CGI category, only show NV materials
							return material.type === "NV";
						} else {
							// For IO or AG categories, only show V materials
							return material.type === "V";
						}
					});

					setMaterials(availableMaterials);
					setFilteredMaterials(availableMaterials);
				} catch (error) {
					// console.error("Error fetching materials:", error);
				} finally {
					setIsLoading(false);
				}
			};

			if (isOpen) {
				fetchMaterials();
			}
		}, [isOpen, storageLocation, category]);

		// Filter materials based on search term
		useEffect(() => {
			const filtered = materials.filter(
				(material) =>
					material.material.toLowerCase().includes(searchTerm.toLowerCase()) ||
					material.description.toLowerCase().includes(searchTerm.toLowerCase())
			);
			setFilteredMaterials(filtered);
		}, [searchTerm, materials]);

		// Calculate pagination values
		const indexOfLastItem = currentPage * itemsPerPage;
		const indexOfFirstItem = indexOfLastItem - itemsPerPage;
		const currentItems = filteredMaterials.slice(
			indexOfFirstItem,
			indexOfLastItem
		);
		const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);

		// Add pagination controls
		const handlePageChange = (pageNumber) => {
			setCurrentPage(pageNumber);
		};

		// Reset to first page when search term changes
		useEffect(() => {
			setCurrentPage(1);
		}, [searchTerm]);

		if (!isOpen) return null;

		return createPortal(
			<div className="fixed inset-0 z-50 overflow-y-auto">
				<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
					<div
						className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
						onClick={onClose}
					/>

					<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
						<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-medium text-gray-900">
									Select Material
								</h3>
								<button
									onClick={onClose}
									className="text-gray-400 hover:text-gray-500"
								>
									<span className="sr-only">Close</span>
									<svg
										className="h-6 w-6"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>

							{/* Search Input */}
							<div className="mb-4">
								<input
									type="text"
									placeholder="Search material or description..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							{isLoading ? (
								<div className="text-center py-4">Loading...</div>
							) : (
								<>
									{/* Desktop View */}
									<div className="hidden md:block overflow-x-auto">
										<table className="w-full divide-y divide-gray-200">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
														Action
													</th>
													<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Material
													</th>
													{(category === "IO" || category === "AG") && (
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Batch
														</th>
													)}
													<th className="hidden md:table-cell px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Description
													</th>
													<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Qty
													</th>
													<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														UoM
													</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{currentItems.map((material) => (
													<tr
														key={material.key}
														className={`hover:bg-gray-50 ${
															material.type === "NV" ? "bg-green-50" : ""
														}`}
													>
														<td className="px-3 py-2 whitespace-nowrap">
															<button
																onClick={() => onSelect(material)}
																className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
															>
																Select
															</button>
														</td>
														<td className="px-3 py-2 whitespace-nowrap text-sm">
															<div>{removeLeadingZeros(material.material)}</div>
															<div className="md:hidden text-xs text-gray-500">
																{material.description}
															</div>
														</td>
														{(category === "IO" || category === "AG") && (
															<td className="px-3 py-2 whitespace-nowrap text-sm">
																{removeLeadingZeros(material.batch) || "-"}
															</td>
														)}
														<td className="hidden md:table-cell px-3 py-2 whitespace-nowrap text-sm text-gray-500">
															{material.description}
														</td>
														<td className="px-3 py-2 whitespace-nowrap text-sm">
															{material.quantity}
														</td>
														<td className="px-3 py-2 whitespace-nowrap text-sm">
															{material.uom}
														</td>
													</tr>
												))}
											</tbody>
										</table>

										{/* Pagination Controls */}
										<div className="px-3 py-4 flex items-center justify-between border-t border-gray-200">
											<div className="flex-1 flex justify-between items-center">
												<div>
													<p className="text-sm text-gray-700">
														Showing{" "}
														<span className="font-medium">
															{indexOfFirstItem + 1}
														</span>{" "}
														to{" "}
														<span className="font-medium">
															{Math.min(
																indexOfLastItem,
																filteredMaterials.length
															)}
														</span>{" "}
														of{" "}
														<span className="font-medium">
															{filteredMaterials.length}
														</span>{" "}
														results
													</p>
												</div>
												<div className="flex space-x-2">
													<button
														onClick={() => handlePageChange(currentPage - 1)}
														disabled={currentPage === 1}
														className={`px-3 py-1 rounded-md text-sm ${
															currentPage === 1
																? "bg-gray-100 text-gray-400 cursor-not-allowed"
																: "bg-white text-blue-600 hover:bg-blue-50 border border-gray-300"
														}`}
													>
														Previous
													</button>
													{Array.from(
														{ length: totalPages },
														(_, i) => i + 1
													).map((pageNumber) => (
														<button
															key={pageNumber}
															onClick={() => handlePageChange(pageNumber)}
															className={`px-3 py-1 rounded-md text-sm ${
																currentPage === pageNumber
																	? "bg-blue-600 text-white"
																	: "bg-white text-blue-600 hover:bg-blue-50 border border-gray-300"
															}`}
														>
															{pageNumber}
														</button>
													))}
													<button
														onClick={() => handlePageChange(currentPage + 1)}
														disabled={currentPage === totalPages}
														className={`px-3 py-1 rounded-md text-sm ${
															currentPage === totalPages
																? "bg-gray-100 text-gray-400 cursor-not-allowed"
																: "bg-white text-blue-600 hover:bg-blue-50 border border-gray-300"
														}`}
													>
														Next
													</button>
												</div>
											</div>
										</div>
									</div>

									{/* Mobile View */}
									<div className="md:hidden">
										<div className="space-y-3">
											{currentItems.map((material) => (
												<Paper
													key={material.key}
													elevation={1}
													sx={{
														bgcolor:
															material.type === "NV" ? "#f0fdf4" : "white", // Tailwind green-50 color
													}}
													className={`p-3 ${
														material.type === "NV"
															? "hover:bg-green-100/80"
															: "hover:bg-gray-50"
													}`}
												>
													<div className="flex justify-between items-start mb-2">
														<div>
															<div className="font-medium">
																{removeLeadingZeros(material.material)}
															</div>
															<div className="text-sm text-gray-600">
																{material.description}
															</div>
														</div>
														<button
															onClick={() => onSelect(material)}
															className="text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded"
														>
															Select
														</button>
													</div>
													<div className="text-sm grid grid-cols-2 gap-2">
														<div>
															<span className="text-gray-500">Qty:</span>{" "}
															{material.quantity} {material.uom}
														</div>
														{(category === "IO" || category === "AG") && (
															<div>
																<span className="text-gray-500">Batch:</span>{" "}
																{removeLeadingZeros(material.batch) || "-"}
															</div>
														)}
														<div>
															<span className="text-gray-500">Plant:</span>{" "}
															{material.plant}
														</div>
														<div>
															<span className="text-gray-500">SLoc:</span>{" "}
															{material.storage_location}
														</div>
													</div>
												</Paper>
											))}
										</div>

										{/* Mobile Pagination Controls */}
										<div className="px-3 py-4 flex flex-col items-center justify-center space-y-3 border-t border-gray-200">
											<p className="text-sm text-gray-700">
												Showing {indexOfFirstItem + 1} to{" "}
												{Math.min(indexOfLastItem, filteredMaterials.length)} of{" "}
												{filteredMaterials.length} results
											</p>
											<div className="flex space-x-2">
												<button
													onClick={() => handlePageChange(currentPage - 1)}
													disabled={currentPage === 1}
													className={`px-3 py-1 rounded-md text-sm ${
														currentPage === 1
															? "bg-gray-100 text-gray-400 cursor-not-allowed"
															: "bg-white text-blue-600 hover:bg-blue-50 border border-gray-300"
													}`}
												>
													Previous
												</button>
												<span className="px-3 py-1 text-sm text-gray-700">
													Page {currentPage} of {totalPages}
												</span>
												<button
													onClick={() => handlePageChange(currentPage + 1)}
													disabled={currentPage === totalPages}
													className={`px-3 py-1 rounded-md text-sm ${
														currentPage === totalPages
															? "bg-gray-100 text-gray-400 cursor-not-allowed"
															: "bg-white text-blue-600 hover:bg-blue-50 border border-gray-300"
													}`}
												>
													Next
												</button>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>,
			document.getElementById("portal-root")
		);
	};

	// Add these handlers in the main component
	const handleMaterialSelect = (material) => {
		const updatedItem = {
			...data.mt_item[selectedItem],
			matnr: material.material,
			maktx: material.description,
			meins: material.uom,
			uom: material.uom,
			charg: material.batch || "",
			werks: data.ms_header.werks_cid, // Copy plant from header
			lgort: data.ms_header.lgort_cid, // Copy storage location from header
		};

		setData((prev) => ({
			...prev,
			mt_item: prev.mt_item.map((item, idx) =>
				idx === selectedItem ? updatedItem : item
			),
		}));
		setIsMaterialSearchOpen(false);
	};

	const handleOrderSelect = (order) => {
		setData((prev) => ({
			...prev,
			mt_item: prev.mt_item.map((item, idx) =>
				idx === selectedItem
					? {
							...item,
							aufnr: order.aufnr,
					  }
					: item
			),
		}));
		setIsOrderSearchOpen(false);
	};

	// Add StorageLocationModal component
	const StorageLocationModal = ({ isOpen, onClose, onSelect }) => {
		if (!isOpen) return null;

		return createPortal(
			<Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
				<div className="p-6">
					<h2 className="text-lg font-medium mb-4">Select Storage Location</h2>
					<div className="space-y-3">
						<MuiButton
							variant="contained"
							onClick={() => onSelect("ST11")}
							fullWidth
							sx={{ textTransform: "none" }}
						>
							ST11
						</MuiButton>
						<MuiButton
							variant="contained"
							onClick={() => onSelect("ST21")}
							fullWidth
							sx={{ textTransform: "none" }}
						>
							ST21
						</MuiButton>
					</div>
				</div>
			</Dialog>,
			document.getElementById("portal-root")
		);
	};

	// Add handler for storage location selection
	const handleStorageLocationSelect = (value) => {
		// Update header storage location
		handleHeaderChange("lgort_cid", value);

		// Update all items' storage location
		setData((prev) => ({
			...prev,
			mt_item: prev.mt_item.map((item) => ({
				...item,
				lgort: value, // Update storage location for each item
			})),
		}));

		setIsStorageLocationOpen(false);
	};

	const handleCancel = () => {
		unlockInquiry();
		setIsEditing(false);
		setData(originalData);
		setNewItemData(null); // Clear any new item form
		setSelectedItem(0); // Reset to first item
	};

	const handleDelete = (index) => {
		const item = data.mt_item[index];

		if (item.isNew) {
			// For new items, remove them from the list
			setData((prev) => ({
				...prev,
				mt_item: prev.mt_item.filter((_, idx) => idx !== index),
			}));
			// Adjust selected item if needed
			if (selectedItem >= index) {
				setSelectedItem(Math.max(0, selectedItem - 1));
			}
		} else {
			// For existing items, mark them for deletion
			const updatedItems = [...data.mt_item];
			updatedItems[index] = {
				...updatedItems[index],
				status: updatedItems[index].status === "D" ? "" : "D",
			};
			setData((prev) => ({
				...prev,
				mt_item: updatedItems,
			}));
		}
	};

	// Function to initialize new blank item
	const handleAddNewItem = () => {
		// Clear the selected item when opening the new item form
		setSelectedItem(-1); // Use -1 to indicate no item is selected
		setNewItemData({
			matnr: "",
			maktx: "",
			menge: "",
			meins: "",
			werks: data.ms_header.werks_cid,
			lgort: data.ms_header.lgort_cid,
			wempf: "",
			aufnr: "",
			sgtxt: "",
			charg: "",
			isNew: true,
			uom: "",
		});
	};

	// Function to handle changes in new item data
	const handleNewItemChange = (field, value) => {
		setNewItemData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	// Function to handle material selection for new item
	const handleNewItemMaterialSelect = (material) => {
		setNewItemData({
			matnr: material.material,
			maktx: material.description,
			meins: material.uom,
			uom: material.uom, // Make sure both meins and uom are set
			menge: "",
			charg: category === "CGI" ? "" : material.batch || "",
			werks: data.ms_header.werks_cid,
			lgort: data.ms_header.lgort_cid,
			wempf: "",
			aufnr: "",
			sgtxt: "",
		});
		setIsMaterialSearchOpen(false);
	};

	// Add new handler for order selection in new item form
	const handleNewItemOrderSelect = (order) => {
		setNewItemData((prev) => ({
			...prev,
			aufnr: order.aufnr,
		}));
		setIsOrderSearchOpen(false);
	};

	// Function to add the new item to the list
	const handleAddItemToList = () => {
		if (!newItemData.matnr) {
			showAlert("Please select a material first");
			return;
		}

		// Add quantity validation
		if (!newItemData.menge || newItemData.menge === "0") {
			showAlert("Please enter a valid quantity");
			return;
		}

		// Create new item with header's plant and storage location
		const newItem = {
			...newItemData,
			werks: data.ms_header.werks_cid,
			lgort: data.ms_header.lgort_cid,
			inqno: inqno,
			inqit: "",
			charg: category === "CGI" ? "" : newItemData.charg,
			isNew: true,
		};

		setData((prev) => {
			const updatedItems = [...prev.mt_item, newItem];
			// Set the selected item to the newly added item
			setSelectedItem(updatedItems.length - 1);
			return {
				...prev,
				mt_item: updatedItems,
			};
		});
		setNewItemData(null);
	};

	if (loading) {
		return <LoadingScreen />;
	}

	if (error) {
		return <div className="text-center text-red-600 py-4">{error}</div>;
	}

	if (!data) return null;

	return (
		<div className="min-h-screen bg-gray-100">
			{isSaving && <LoadingOverlay />}
			<nav className="bg-white shadow-lg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between h-16">
						<div className="flex items-center overflow-hidden">
							<h1 className="text-base sm:text-xl font-bold text-gray-900 truncate pr-4">
								Sparepart Detail #{removeLeadingZeros(inqno)}{" "}
								{category === "CGI" ? "(NV)" : `(${category})`}
							</h1>
							{lockError && (
								<span className="text-sm text-red-600">{lockError}</span>
							)}
						</div>
						<div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
							{!isEditing ? (
								<>
									<button
										onClick={handleEdit}
										className="px-3 sm:px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
									>
										Edit
									</button>
									<button
										onClick={handleBack}
										className="px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
									>
										Back
									</button>
								</>
							) : (
								<div className="flex items-center space-x-2">
									<button
										onClick={handleEdit}
										disabled={isSaving}
										className={`px-3 sm:px-4 py-2 text-sm text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
											isSaving
												? "bg-green-400 cursor-not-allowed"
												: "bg-green-600 hover:bg-green-700 focus:ring-green-500"
										} inline-flex items-center`}
									>
										{isSaving ? (
											<>
												<svg
													className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
													></circle>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													></path>
												</svg>
												<span>Saving...</span>
											</>
										) : (
											<span>Save</span>
										)}
									</button>
									<button
										onClick={handleCancel}
										disabled={isSaving}
										className={`px-3 sm:px-4 py-2 text-sm rounded-md ${
											isSaving
												? "text-gray-400 bg-gray-100 cursor-not-allowed"
												: "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
										}`}
									>
										Cancel
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</nav>

			<main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
				<div className="px-4 py-6 sm:px-0">
					<div className="bg-white shadow rounded-lg">
						{/* Header Information - Reordered */}
						<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
							<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
								<TextField
									label="Item Total"
									value={data?.mt_item?.length || 0}
									disabled={true}
									width="16"
									className="opacity-75"
								/>
								<DatePickerField
									label="Document Date"
									value={data?.ms_header?.bldat}
									onChange={(date) =>
										handleHeaderChange(
											"bldat",
											date.toISOString().split("T")[0]
										)
									}
									disabled={!isEditing}
									className="opacity-75"
								/>
								<TextField
									label="Plant"
									value={data?.ms_header?.werks_cid}
									onChange={() => {}}
									disabled={true}
								/>
								<TextField
									label="Storage Location"
									value={data?.ms_header?.lgort_cid}
									onChange={() => {}}
									disabled={true}
									rightIcon={
										isEditing &&
										(!data?.mt_item?.length || data?.mt_item?.length === 0) ? (
											<MaterialSearchIcon
												onClick={() => setIsStorageLocationOpen(true)}
											/>
										) : null
									}
									forceWhite={
										isEditing &&
										(!data?.mt_item?.length || data?.mt_item?.length === 0)
									}
								/>
								<TextField
									label="Material Slip"
									value={data?.ms_header?.xblnr}
									onChange={(value) => handleHeaderChange("xblnr", value)}
									disabled={isTextFieldDisabled}
									maxLength={16}
								/>
								<TextField
									label="Doc Header Text"
									value={data?.ms_header?.bktxt}
									onChange={
										isEditing
											? (value) => handleHeaderChange("bktxt", value)
											: undefined
									}
									disabled={!isEditing}
									maxLength={25}
								/>
							</div>
						</div>

						{/* Item Details */}
						{newItemData ? (
							// New Item Form
							<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-medium text-gray-900">
										New Item
									</h3>
									<div className="space-x-2">
										<button
											onClick={handleAddItemToList}
											className="px-3 py-1 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md"
										>
											Add to List
										</button>
										<button
											onClick={() => {
												setNewItemData(null);
												setSelectedItem(Math.min(data.mt_item.length - 1, 0));
											}}
											className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
										>
											Cancel
										</button>
									</div>
								</div>
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
									<div className="sm:col-span-2">
										<TextField
											label="Material"
											value={removeLeadingZeros(newItemData.matnr)}
											onChange={() => {}}
											disabled={false}
											readOnly={true}
											forceWhite={true}
											onClick={
												!isSaving ? handleMaterialSearchClick : undefined
											}
											rightIcon={
												<MaterialSearchIcon
													onClick={
														!isSaving ? handleMaterialSearchClick : undefined
													}
												/>
											}
										/>
										{newItemData.matnr && (
											<div className="mt-1">
												<span className="text-xs text-gray-500">
													{newItemData.maktx}
												</span>
											</div>
										)}
									</div>
									<div className="sm:col-span-2">
										<TextField
											label="Batch"
											value={newItemData.charg}
											disabled={true}
										/>
									</div>
									<div className="flex gap-2 sm:col-span-2">
										<div className="w-2/3">
											<TextField
												label="Quantity"
												value={newItemData.menge}
												onChange={(value) =>
													handleNewItemChange("menge", value)
												}
												width="full"
											/>
										</div>
										<div className="w-1/3">
											<TextField
												label="UoM"
												value={newItemData.meins}
												disabled={true}
												width="full"
											/>
										</div>
									</div>
									<div className="sm:col-span-2">
										<TextField
											label="Recipient"
											value={newItemData.wempf}
											onChange={(value) => handleNewItemChange("wempf", value)}
											maxLength={12}
										/>
									</div>
									{(category === "IO" || category === "CGI") && (
										<div className="sm:col-span-4">
											<TextField
												label="Order"
												value={newItemData.aufnr}
												onChange={() => {}}
												disabled={false}
												readOnly={true}
												forceWhite={true}
												onClick={
													!isSaving
														? () => setIsOrderSearchOpen(true)
														: undefined
												}
												rightIcon={
													<div className="flex items-center space-x-1">
														{category === "CGI" && newItemData.aufnr && (
															<button
																onClick={() => handleNewItemChange("aufnr", "")}
																className="w-4 h-4 text-gray-500 hover:text-gray-700"
															>
																<XMarkIcon className="w-4 h-4" />
															</button>
														)}
														<MaterialSearchIcon
															onClick={
																!isSaving
																	? () => setIsOrderSearchOpen(true)
																	: undefined
															}
														/>
													</div>
												}
											/>
										</div>
									)}
									<div className="sm:col-span-4">
										<TextField
											label="Text"
											value={newItemData.sgtxt}
											onChange={(value) => handleNewItemChange("sgtxt", value)}
											maxLength={50}
										/>
									</div>
								</div>
							</div>
						) : (
							!newItemData && data?.mt_item?.length > 0 && selectedItem >= 0 && (
								<div className="px-4 py-5 sm:p-6 border-b border-gray-200">
									<h3 className="text-lg font-medium text-gray-900 mb-4">
										Item Details
									</h3>
									<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
										<TextField
											label="Item"
											value={(selectedItem + 1).toString().padStart(2, "0")}
											disabled={true}
											width="16"
											className="opacity-75"
										/>
										<div className="sm:col-span-3">
											<TextField
												label="Material"
												value={removeLeadingZeros(
													data.mt_item[selectedItem].matnr
												)}
												onChange={() => {}}
												disabled={false}
												readOnly={true}
												forceWhite={isEditing}
												onClick={
													isEditing && !isSaving
														? handleMaterialSearchClick
														: undefined
												}
												rightIcon={
													<MaterialSearchIcon
														onClick={
															isEditing && !isSaving
																? handleMaterialSearchClick
																: undefined
														}
													/>
												}
											/>
											<div className="mt-1">
												<span className="text-xs text-gray-500">
													{data.mt_item[selectedItem].maktx}
												</span>
											</div>
										</div>
										<div className="sm:col-span-2">
											<TextField
												label="Batch"
												value={
													removeLeadingZeros(
														data.mt_item[selectedItem].charg
													) || "-"
												}
												disabled={true}
											/>
										</div>
										<div className="flex gap-2 sm:col-span-2">
											<div className="w-2/3">
												<TextField
													label="Quantity"
													value={data.mt_item[selectedItem].menge}
													onChange={
														isEditing
															? (value) => handleItemChange("menge", value)
															: undefined
													}
													disabled={!isEditing}
													width="full"
												/>
											</div>
											<div className="w-1/3">
												<TextField
													label="UoM"
													value={data.mt_item[selectedItem].uom}
													disabled={true}
													width="full"
												/>
											</div>
										</div>
										<div className="sm:col-span-2">
											<TextField
												label="Recipient"
												value={data.mt_item[selectedItem].wempf}
												onChange={
													isEditing
														? (value) => handleItemChange("wempf", value)
														: undefined
												}
												disabled={!isEditing}
												maxLength={12}
											/>
										</div>
										{(category === "IO" || category === "CGI") && (
											<div className="sm:col-span-4">
												<TextField
													label="Order"
													value={data.mt_item[selectedItem].aufnr}
													onChange={() => {}}
													disabled={false}
													readOnly={true}
													forceWhite={isEditing}
													onClick={
														isEditing && !isSaving
															? () => setIsOrderSearchOpen(true)
															: undefined
													}
													rightIcon={
														<div className="flex items-center space-x-1">
															{category === "CGI" &&
																data.mt_item[selectedItem].aufnr &&
																isEditing &&
																!isSaving && (
																	<ClearIcon
																		onClick={(e) => {
																			e.stopPropagation();
																			handleItemChange("aufnr", "");
																		}}
																	/>
																)}
															<MaterialSearchIcon
																onClick={
																	isEditing && !isSaving
																		? () => setIsOrderSearchOpen(true)
																		: undefined
																}
															/>
														</div>
													}
												/>
											</div>
										)}
										<div className="sm:col-span-4">
											<TextField
												label="Text"
												value={data.mt_item[selectedItem].sgtxt}
												onChange={
													isEditing
														? (value) => handleItemChange("sgtxt", value)
														: undefined
												}
												disabled={!isEditing}
												maxLength={50}
											/>
										</div>
									</div>
								</div>
							)
						)}

						{/* Items List */}
						<div className="px-4 py-5 sm:p-6">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-medium text-gray-900">
									Items List
								</h3>
								{isEditing && !newItemData && !isSaving && (
									<button
										onClick={handleAddNewItem}
										className="px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
									>
										Add New Item
									</button>
								)}
							</div>

							{/* Desktop View */}
							<div className="hidden md:block overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Item
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Material
											</th>
											{(category === "IO" || category === "AG") && (
												<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
													Batch
												</th>
											)}
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Quantity
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												UoM
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Plant
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												SLoc
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Recipient
											</th>
											{(category === "IO" || category === "CGI") && (
												<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
													Order
												</th>
											)}
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Text
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
												Actions
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{data?.mt_item.map((item, index) => (
											<tr
												key={item.inqit}
												className={`cursor-pointer ${
													item.status === "D"
														? "bg-red-50 hover:bg-red-100"
														: selectedItem === index
														? "bg-blue-50 hover:bg-blue-100"
														: "hover:bg-gray-50"
												}`}
												onClick={() => setSelectedItem(index)}
											>
												<td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600">
													{(index + 1).toString().padStart(2, "0")}
												</td>
												<td className="px-4 py-2 text-sm">
													<div>{removeLeadingZeros(item.matnr)}</div>
													<div className="text-xs text-gray-500">
														{item.maktx}
													</div>
												</td>
												{(category === "IO" || category === "AG") && (
													<td className="px-4 py-2 whitespace-nowrap text-sm">
														{removeLeadingZeros(item.charg) || "-"}
													</td>
												)}
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{item.menge}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{item.uom}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{item.werks}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{item.lgort}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{item.wempf || "-"}
												</td>
												{(category === "IO" || category === "CGI") && (
													<td className="px-4 py-2 whitespace-nowrap text-sm">
														{item.aufnr}
													</td>
												)}
												<td className="px-4 py-2 text-sm">
													{item.sgtxt || "-"}
												</td>
												<td className="px-4 py-2 whitespace-nowrap text-sm">
													{isEditing && !isSaving && (
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleDelete(index);
															}}
															className={`inline-flex items-center px-2 py-1 rounded-md ${
																item.isNew
																	? "text-red-600 hover:text-red-900"
																	: item.status === "D"
																	? "text-green-600 hover:text-green-900"
																	: "text-red-600 hover:text-red-900"
															}`}
														>
															{item.isNew ? (
																<XMarkIcon className="h-5 w-5" />
															) : item.status === "D" ? (
																<ArrowUturnLeftIcon className="h-5 w-5" />
															) : (
																<TrashIcon className="h-5 w-5" />
															)}
															<span className="ml-1">
																{item.isNew
																	? "Remove"
																	: item.status === "D"
																	? "Undelete"
																	: "Delete"}
															</span>
														</button>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Mobile view */}
							<div className="md:hidden">
								<div className="space-y-2">
									{data?.mt_item.map((item, index) => (
										<div
											key={item.inqit}
											className={`border rounded-lg p-4 ${
												item.status === "D"
													? "bg-red-50 hover:bg-red-100"
													: selectedItem === index
													? "bg-blue-50 border-blue-200"
													: "bg-white hover:bg-gray-50"
											}`}
											onClick={() => setSelectedItem(index)}
										>
											{/* Header with Item number and Quantity/UoM/Delete */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-blue-600">
													Item {(index + 1).toString().padStart(2, "0")}
												</span>
												<div className="flex items-center space-x-2">
													<span className="text-sm">
														{item.menge} {item.uom}
													</span>
													{isEditing && !isSaving && (
														<button
															onClick={(e) => {
																e.stopPropagation();
																handleDelete(index);
															}}
															className={`p-1 rounded-md ${
																item.isNew
																	? "text-red-600"
																	: item.status === "D"
																	? "text-green-600"
																	: "text-red-600"
															}`}
														>
															{item.isNew ? (
																<XMarkIcon className="h-4 w-4" />
															) : item.status === "D" ? (
																<ArrowUturnLeftIcon className="h-4 w-4" />
															) : (
																<TrashIcon className="h-4 w-4" />
															)}
														</button>
													)}
												</div>
											</div>

											{/* Material and Description */}
											<div className="mb-2">
												<div className="flex justify-between items-center">
													<span className="text-sm text-gray-500">
														Material
													</span>
													<span className="text-sm">
														{removeLeadingZeros(item.matnr)}
													</span>
												</div>
												<div className="text-sm text-gray-600 mt-1">
													{item.maktx}
												</div>
											</div>

											{/* Batch - only for IO or AG */}
											{(category === "IO" || category === "AG") && (
												<div className="flex justify-between items-center mb-2">
													<span className="text-sm text-gray-500">Batch</span>
													<span className="text-sm">
														{removeLeadingZeros(item.charg) || "-"}
													</span>
												</div>
											)}

											{/* Plant/SLoc */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-gray-500">
													Plant/SLoc
												</span>
												<span className="text-sm">
													{item.werks}/{item.lgort}
												</span>
											</div>

											{/* Recipient */}
											<div className="flex justify-between items-center mb-2">
												<span className="text-sm text-gray-500">Recipient</span>
												<span className="text-sm">{item.wempf || "-"}</span>
											</div>

											{/* Add Order field for IO or CGI category */}
											{(category === "IO" || category === "CGI") && (
												<div className="flex justify-between items-center mb-2">
													<span className="text-sm text-gray-500">Order</span>
													<span className="text-sm">{item.aufnr || "-"}</span>
												</div>
											)}

											{/* Text */}
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-500">Text</span>
												<span className="text-sm">{item.sgtxt || "-"}</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>

			{/* Add these modal components */}
			<MaterialSearchModal
				isOpen={isMaterialSearchOpen}
				onClose={() => setIsMaterialSearchOpen(false)}
				onSelect={
					newItemData ? handleNewItemMaterialSelect : handleMaterialSelect
				}
				plant={data?.ms_header?.werks_cid}
				storageLocation={data?.ms_header?.lgort_cid}
				category={category}
				existingItems={data?.mt_item}
			/>

			<OrderSearchModal
				isOpen={isOrderSearchOpen}
				onClose={() => setIsOrderSearchOpen(false)}
				onSelect={newItemData ? handleNewItemOrderSelect : handleOrderSelect}
			/>

			<StorageLocationModal
				isOpen={isStorageLocationOpen}
				onClose={() => setIsStorageLocationOpen(false)}
				onSelect={handleStorageLocationSelect}
			/>

			{/* Alert Dialog */}
			<Dialog
				open={alertDialog.isOpen}
				onClose={() => setAlertDialog((prev) => ({ ...prev, isOpen: false }))}
			>
				<div
					className={`p-6 ${
						alertDialog.type === "error" ? "bg-red-50" : "bg-green-50"
					}`}
				>
					<div
						className={`text-${
							alertDialog.type === "error" ? "red" : "green"
						}-600`}
					>
						{alertDialog.message}
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default SparepartDetail;
